import { Injectable } from '@angular/core';
import { InstructionsModalComponent } from '@common/containers/instructions-modal/instructions-modal.component';
import { EmbeddedVideoOptions } from '@common/interfaces';
import { UserService } from '@modules/auth/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { AnalyticsService } from './analytics.service';
import { InstructionsService, MAX_TUTORIAL_VIEWS } from './instructions.service';
import { ToastService } from './toast.service';

@Injectable({
    providedIn: 'root',
})
export class InstructionsModalService {
    modalRef: any;
    instructionsRequired: string[] = ['direct-sales-es-inventory', 'direct-sales-es-sales-channels', 'sales-channels-es-inventory', 'product-template-es-inventory'];
    loginQuantity: number;

    // Visibilidad del modal instructivo. Se marca `true` de forma síncrona apenas se decide mostrarlo
    // (antes del HTTP), y `false` al cerrarse/errores. Lo consume el TourOrchestratorService para encolar
    // el Blackout y no lanzarlo encima de un modal abierto.
    private readonly _modalOpen$ = new BehaviorSubject<boolean>(false);
    readonly modalOpen$: Observable<boolean> = this._modalOpen$.asObservable();
    get isModalOpen(): boolean {
        return this._modalOpen$.value;
    }

    // Supresión del auto-show del video para onboarding Retail (Fase LL). Lo REGISTRA el
    // TourOrchestratorService (que sí conoce el onboarding); este servicio NO importa onboarding — solo
    // consulta el predicado (Cero Cables). Devuelve true cuando el módulo actual tiene una tarea de onboarding
    // PENDIENTE con auto_start → el video de bienvenida se suprime (el blackout ya guía, incluido el paso del
    // botón de ayuda). Con la tarea completada / no-Retail devuelve false → el video se muestra normal.
    private _videoSuppressionCheck: (() => Observable<boolean>) | null = null;

    // Predicado keyed (Cero Cables): dado el `instructionsSetName` del video, ¿su tarea de onboarding YA está
    // completada? Lo REGISTRA el TourOrchestratorService (que conoce el mapa task↔video). Cuando devuelve true,
    // `showModal` NO auto-muestra el video y marca "no volver a mostrar" para esa key (silencioso). Este
    // servicio NO importa onboarding — solo consulta el predicado. Con tarea pendiente / sin mapa → false.
    private _completedVideoCheck: ((videoKey: string) => Observable<boolean>) | null = null;

    constructor(
        private modalService: NgbModal,
        private instructionsService: InstructionsService,
        private userService: UserService,
        private toastService: ToastService,
        private analyticsService: AnalyticsService,
    ) {}

    async showModal(instructionsSetName: string, videoOptions?: EmbeddedVideoOptions, bypassNotShow: boolean = false) {
        try {
            // Drenar vistas encoladas de sesiones/intentos anteriores (respaldo del fail-open).
            this.instructionsService.flushPendingViews();

            // SUPRESIÓN (onboarding Retail, Fase LL): si en la ruta actual hay una tarea de onboarding PENDIENTE
            // con auto_start, el blackout guía al usuario (incluido el paso "Consulta un video"), así que NO
            // abrimos el auto-show del video de bienvenida. Solo aplica al auto-show (bypassNotShow=false): el
            // clic manual en "Ayuda" (bypass=true) SIEMPRE muestra el video. Tarea completada / no-Retail →
            // el predicado devuelve false → el video se muestra normal (como hoy).
            // TAREA DE ONBOARDING COMPLETADA (auto-show): si esta key corresponde a una tarea de onboarding YA
            // completada, NO auto-mostramos el video y registramos programáticamente "no volver a mostrar" (como
            // si el usuario hubiese marcado el checkbox), para no molestarlo en el futuro. Solo auto-show
            // (bypassNotShow=false): el clic manual en "Ayuda" (bypass=true) SIEMPRE muestra el video.
            // Los predicados devuelven Observable<boolean> (el orquestador DIFIERE la decisión hasta que el estado
            // del onboarding resuelve — Se resuelven de forma asíncrona
            // (una sola emisión) ANTES de abrir el video; solo se abre si el resultado final es `false` (no
            // suprimir). El auto-show queda a la espera de esa decisión; el clic manual (bypassNotShow=true) la
            // salta por completo y abre el video al instante.
            if (!bypassNotShow && this._completedVideoCheck) {
                const isCompletedTaskVideo = await this.resolveSuppressionHook(this._completedVideoCheck(instructionsSetName));
                if (isCompletedTaskVideo) {
                    this.markDoNotShowAgainSilently(instructionsSetName);
                    return;
                }
            }

            if (!bypassNotShow && this._videoSuppressionCheck) {
                const isEntryVideoSuppressed = await this.resolveSuppressionHook(this._videoSuppressionCheck());
                if (isEntryVideoSuppressed) {
                    return;
                }
            }

            // const loginResponse = await firstValueFrom(this.userService.getLoginQuantity$());
            // this.loginQuantity = loginResponse.data.loginAmount;

            // if(this.loginQuantity === 1) return;

            // Check if modal should be blocked before subscribing
            const user = this.userService.user$.getValue();
            const dismissedByUser = user?.instructionsNotToShow?.includes(instructionsSetName);
            const reachedViewsLimit = (user?.instructionsViews?.[instructionsSetName] ?? 0) >= MAX_TUTORIAL_VIEWS;
            const shouldBlock = !bypassNotShow && (dismissedByUser || reachedViewsLimit) && !this.instructionsRequired.includes(instructionsSetName);

            if (shouldBlock) {
                return; // Exit early if modal should be blocked
            }

            // Señal SÍNCRONA (antes del HTTP): el modal se mostrará. El orquestador de tours ya la ve al
            // procesar NavigationEnd y encola el Blackout. Se revierte a false en cierre o error (abajo).
            this._modalOpen$.next(true);

            const getInstructionsSubscription = this.instructionsService.getInstructionsSetByName(instructionsSetName).subscribe({
                next: response => {
                    const { title, videoUrl, steps, text, lock } = response.data;

                    let block = {};
                    if (lock) block = { backdrop: 'static', keyboard: false };
                    this.analyticsService.analyticsCapture('onboarding_mostrar modal', { modulo: title });
                    this.analyticsService.startSessionRecording();
                    this.modalRef = this.modalService.open(InstructionsModalComponent, {
                        centered: true,
                        size: lock ? 'md' : 'xl',
                        ...block,
                        beforeDismiss: () => false,
                    });

                    const { autoplay, startTime, muted } = videoOptions || {};
                    this.modalRef.componentInstance.videoUrl = `${videoUrl}?${this.getEmbedOptionsParams({ autoplay, startTime, muted })}`;
                    this.modalRef.componentInstance.instructions = [...steps];
                    this.modalRef.componentInstance.text = text;
                    this.modalRef.componentInstance.lock = lock;
                    this.modalRef.componentInstance.modalTitle = title;
                    this.modalRef.componentInstance.modalIconTitle = '';
                    this.modalRef.componentInstance.instructionsSetName = instructionsSetName;

                    // Solo cuentan las apariciones automáticas: las aperturas manuales
                    // (bypassNotShow) y los sets requeridos no suman al límite.
                    if (!bypassNotShow && !this.instructionsRequired.includes(instructionsSetName)) {
                        this.instructionsService.registerViewTracked(instructionsSetName);
                    }

                    this.modalRef.result
                        .then(({ doNotShowAgain }: any) => {
                            if (doNotShowAgain) {
                                this.instructionsService.doNotShowAgain(instructionsSetName).subscribe({
                                    next: () => {
                                        const user = this.userService.user$.getValue();
                                        user.instructionsNotToShow = [...user.instructionsNotToShow, instructionsSetName];
                                        this.userService.user = user;
                                        this.toastService.success('¡Hecho!', 'Esta ayuda no se mostrará de nuevo.');
                                    },
                                    error: (err: any) => {
                                        console.error(err);
                                        this.toastService.error('¡Ups!', 'Por los momentos no se ha podido guardar tu preferencia.');
                                    },
                                });
                            }
                        })
                        .catch(() => {
                            // Dismiss (raro: beforeDismiss:()=>false). No-op; el finally revierte la señal.
                        })
                        .finally(() => this._modalOpen$.next(false)); // el modal se cerró → libera el tour encolado
                },

                error: (err: any) => {
                    console.error(err);
                    if (this.modalRef) {
                        this.modalRef.close();
                    }
                    this.modalRef = null;
                    this._modalOpen$.next(false); // el modal no llegó a abrir → no bloquear el tour
                },
            });
        } catch (err: any) {
            // Excepción síncrona tras marcar el modal como abierto: liberar el gate para que el
            // TourOrchestratorService no quede esperando eternamente el cierre de un modal que nunca abrió.
            this._modalOpen$.next(false);
            if (err?.error?.title && err?.error?.message) {
                this.toastService.error(err.error.title, err.error.message);
            } else {
                console.error(err);
            }
        }
    }

    private getEmbedOptionsParams({ hideEmbedTopBar, startTime, muted, autoplay, hide_share, hide_title, hide_owner }: any = {}) {
        const embedOptions = {
            hideEmbedTopBar: typeof hideEmbedTopBar === 'boolean' ? hideEmbedTopBar : true,
            t: typeof startTime === 'string' ? startTime : '0s',
            muted: typeof muted === 'boolean' ? muted : false,
            autoplay: typeof autoplay === 'boolean' ? autoplay : false,
            hide_share: typeof hide_share === 'boolean' ? hide_share : true,
            hide_title: typeof hide_title === 'boolean' ? hide_title : true,
            hide_owner: typeof hide_owner === 'boolean' ? hide_owner : true,
        };

        let embedVideoParams = new URLSearchParams();
        for (const [key, value] of Object.entries(embedOptions)) {
            embedVideoParams.append(key, value.toString());
        }

        return embedVideoParams.toString();
    }

    forceCloseModal() {
        if (this.modalRef) {
            this.modalRef.close();
            this.modalRef = null;
        }
        this._modalOpen$.next(false); // libera cualquier tour encolado al forzar el cierre
    }

    /**
     * Registra el predicado de supresión del auto-show del video (Fase LL). Lo llama el TourOrchestratorService
     * en su constructor. Mantiene Cero Cables: la decisión (onboarding) vive fuera; este servicio solo la
     * consulta en `showModal`.
     */
    registerVideoSuppressionCheck(check: () => Observable<boolean>): void {
        this._videoSuppressionCheck = check;
    }

    /**
     * Registra el predicado que decide, por `instructionsSetName`, si la tarea de onboarding asociada a ese
     * video ya está completada. Lo llama el TourOrchestratorService en su constructor. Mantiene Cero Cables:
     * la decisión (onboarding) vive fuera; este servicio solo la consulta en `showModal`.
     */
    registerCompletedVideoCheck(check: (videoKey: string) => Observable<boolean>): void {
        this._completedVideoCheck = check;
    }

    /**
     * Resuelve un predicado de supresión asíncrono (`Observable<boolean>`) a un `boolean` para el flujo de
     * `showModal`. Toma la PRIMERA emisión y se desuscribe (sin leaks). Blindado: si el stream se completa sin
     * emitir → `false`; si emite error → `false`. En ambos "peor caso" NO se suprime (el video se abre), que es
     * el fallback seguro pedido por QA (CN-03: API lenta/caída no debe dejar la pantalla bloqueada). Cero Cables:
     * este servicio no sabe QUÉ observa el predicado, solo lo consume.
     */
    private async resolveSuppressionHook(check$: Observable<boolean>): Promise<boolean> {
        try {
            return await firstValueFrom(check$, { defaultValue: false });
        } catch {
            return false;
        }
    }

    /**
     * Marca programáticamente "No volver a mostrar" para `instructionsSetName` (sin UI): persiste en backend y
     * actualiza el usuario en memoria, exactamente como cuando el usuario cierra el modal con el checkbox
     * marcado — pero SIN toast (no interactuó). Idempotente: si ya estaba marcado, no repite la llamada.
     */
    markDoNotShowAgainSilently(instructionsSetName: string): void {
        const user = this.userService.user$.getValue();
        if (!user || user.instructionsNotToShow?.includes(instructionsSetName)) return;
        this.instructionsService.doNotShowAgain(instructionsSetName).subscribe({
            next: () => {
                const fresh = this.userService.user$.getValue();
                if (!fresh) return;
                fresh.instructionsNotToShow = [...(fresh.instructionsNotToShow || []), instructionsSetName];
                this.userService.user = fresh;
            },
            error: (err: any) => console.error('[InstructionsModal] no se pudo guardar doNotShowAgain programático:', err),
        });
    }
}
